import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { User, Prescription, Strategy, TierName } from '../types';

const STORAGE_KEY = 'paladin-app-state';

interface AppStateContext {
  user: User | null;
  setUser: (user: User | null) => void;
  updateUser: (updates: Partial<User>) => void;
  prescription: Prescription | null;
  setPrescription: (rx: Prescription | null) => void;
  savedPrescriptions: Prescription[];
  savePrescription: (rx: Prescription) => void;
  isAuthenticated: boolean;
  logout: () => void;
}

const Context = createContext<AppStateContext | null>(null);

function loadPersistedState(): { user: User | null; savedPrescriptions: Prescription[] } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        user: parsed.user ?? null,
        savedPrescriptions: parsed.savedPrescriptions ?? [],
      };
    }
  } catch {}
  return { user: null, savedPrescriptions: [] };
}

export function AppStateProvider({ children }: { children: ReactNode }) {
  const persisted = loadPersistedState();
  const [user, setUserState] = useState<User | null>(persisted.user);
  const [prescription, setPrescription] = useState<Prescription | null>(null);
  const [savedPrescriptions, setSavedPrescriptions] = useState<Prescription[]>(persisted.savedPrescriptions);

  // Persist user + saved prescriptions to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ user, savedPrescriptions }));
    } catch {}
  }, [user, savedPrescriptions]);

  const setUser = useCallback((u: User | null) => {
    setUserState(u);
  }, []);

  const updateUser = useCallback((updates: Partial<User>) => {
    setUserState(prev => {
      if (!prev) {
        // Bootstrap a minimal user if none exists yet
        return {
          id: `user-${Date.now()}`,
          discordId: '',
          discordUsername: '',
          role: 'Accelerator' as const,
          joinedAt: new Date().toISOString().split('T')[0],
          accountSize: 50000,
          currentTier: null,
          currentStrategy: null,
          currentFirmId: null,
          currentPlanId: null,
          stats: {
            totalTrades: 0,
            winCount: 0,
            avgWinner: 0,
            avgLoser: 0,
            accountDrawdown: 0,
            winRate: 0,
            expectancy: 0,
          },
          traderStage: null,
          monthsTrading: 0,
          challengeStatus: null,
          ...updates,
        } as User;
      }
      return { ...prev, ...updates };
    });
  }, []);

  const savePrescription = useCallback((rx: Prescription) => {
    setSavedPrescriptions(prev => {
      const exists = prev.findIndex(p => p.id === rx.id);
      if (exists >= 0) {
        const next = [...prev];
        next[exists] = rx;
        return next;
      }
      return [...prev, rx];
    });
  }, []);

  const logout = useCallback(() => {
    setUserState(null);
    setPrescription(null);
    setSavedPrescriptions([]);
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  }, []);

  return (
    <Context.Provider value={{
      user,
      setUser,
      updateUser,
      prescription,
      setPrescription,
      savedPrescriptions,
      savePrescription,
      isAuthenticated: user !== null,
      logout,
    }}>
      {children}
    </Context.Provider>
  );
}

export function useAppState(): AppStateContext {
  const ctx = useContext(Context);
  if (!ctx) throw new Error('useAppState must be used within AppStateProvider');
  return ctx;
}
